import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { secureApi } from '../../api/secure';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { projectKeys } from './projectKeys';
import {
  Project,
  ProjectData,
  CreateProjectInput,
  UpdateProjectInput,
  SharedStatusResponse,
  formatProject,
} from './projectTypes';
import { getAvatarUrl, resizeImage } from '../../lib/utils';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { invalidateQuery, safeUpdateQueryData } from '../../utils/queryInvalidationUtils';
import { useUser } from './useAuth';
import { ALLOWED_IMAGE_TYPES } from '../../constants/app';

/**
 * Hook for fetching all projects based on archived status
 * Supports offline mode by using IndexedDB when offline
 * Includes real-time subscriptions for automatic updates
 */
export function useProjects(showArchived = false) {
  const { data: user, isLoading: userLoading } = useUser();
  const userId = user?.id;
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  // Standard React Query hook for initial data and refetching
  const query = useQuery({
    queryKey: projectKeys.list({ archived: showArchived }),
    enabled: !userLoading && !!userId, // Only run this query when user auth is done loading and user exists
    queryFn: async () => {
      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting project fetch #${fetchId} (online: ${isOnline})`);
      const startTime = performance.now();

      if (!userId) {
        const error = new Error('No authenticated user');
        // Use our error handler to standardize and log the error
        handleError(error, {
          action: 'silent',
          context: { fetchId, operation: 'fetchProjects' },
        });
        throw error;
      }

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching projects from IndexedDB #${fetchId}`);

          try {
            // Get projects from IndexedDB
            const projects = await db.projects
              .where('archived')
              .equals(showArchived)
              .and(project => !project.isDeleted) // Filter out deleted projects
              .toArray();

            // Get owned projects
            const ownedProjects = projects.filter(p => String(p.user_id) === String(userId));

            // For offline, we can't accurately determine shared projects without server
            // We'll use projects we have in IndexedDB that aren't owned by current user
            const sharedProjects = projects.filter(p => String(p.user_id) !== String(userId));

            // Format projects
            const formattedProjects = [...ownedProjects, ...sharedProjects].map(project => {
              const formatted = formatProject(project, userId);

              // Set shared status based on ownership
              formatted.isShared = !formatted.isOwner;

              // Apply avatar URL formatting
              formatted.avatar_url = getAvatarUrl(project.avatar_url, project.avatar_updated_at);

              return formatted;
            });

            // Sort by last selected time
            formattedProjects.sort((a, b) => {
              if (!a.last_selected_at) return 1;
              if (!b.last_selected_at) return -1;
              return (
                new Date(b.last_selected_at).getTime() - new Date(a.last_selected_at).getTime()
              );
            });

            logger.fetch(
              `Offline mode: loaded ${formattedProjects.length} projects from IndexedDB`
            );
            return formattedProjects;
          } catch (offlineError) {
            logger.error(`Error fetching offline projects #${fetchId}:`, offlineError);
            throw new Error('Failed to load projects from offline storage');
          }
        }

        // Online flow - fetch from server
        // Start multiple fetch operations in parallel
        const ownedProjectsPromise = supabase
          .from('projects')
          .select('*')
          .eq('user_id', userId)
          .eq('archived', showArchived)
          .order('last_selected_at', { ascending: false, nullsLast: true });

        const sharedProjectsPromise = supabase
          .from('project_collaborators')
          .select('projects(*)')
          .eq('user_id', userId)
          .eq('projects.archived', showArchived);

        // Wait for basic project data to load
        const [ownedProjectsResult, sharedProjectsResult] = await Promise.all([
          ownedProjectsPromise,
          sharedProjectsPromise,
        ]);

        // Check for errors
        if (ownedProjectsResult.error) throw ownedProjectsResult.error;
        if (sharedProjectsResult.error) throw sharedProjectsResult.error;

        const ownedProjects = ownedProjectsResult.data || [];
        const sharedProjects = sharedProjectsResult.data || [];

        // Extract actual project data from shared projects
        const extractedSharedProjects = sharedProjects.map(sp => sp.projects).filter(Boolean);

        // Combine projects
        const allProjects = [...ownedProjects, ...extractedSharedProjects];

        // Remove duplicates
        const uniqueProjects = Array.from(new Map(allProjects.map(p => [p.id, p])).values());

        // Store in IndexedDB for offline access
        try {
          await db.projects.bulkPut(uniqueProjects);
          logger.fetch(`Stored ${uniqueProjects.length} projects in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error storing projects in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Get project IDs for shared status
        const allProjectIds = uniqueProjects.map(project => project.id);

        // Format projects with the helper function - with consistent handling
        const formattedProjects = uniqueProjects.map(project => {
          // Format the project with owner and shared status
          const formatted = formatProject(project as ProjectData, userId);

          // For non-owners, we know it's shared. For owners, we'll assume false initially
          // and update after the shared status API call
          if (!formatted.isOwner) {
            formatted.isShared = true;
          }

          // Apply avatar URL formatting
          formatted.avatar_url = getAvatarUrl(project.avatar_url, project.avatar_updated_at);

          return formatted;
        });

        // Fetch shared status for owner projects
        try {
          const projectsSharedStatus: SharedStatusResponse =
            await secureApi.getProjectsSharedStatus(allProjectIds);

          logger.fetch(`Fetched shared status for ${allProjectIds.length} projects`, {
            sharedProjectsCount: Object.values(projectsSharedStatus).filter(v => v).length,
          });

          // Update projects with accurate shared status
          const updatedProjects = formattedProjects.map(project => {
            if (!project.isOwner) return project; // Non-owner projects already have isShared=true

            // Update owner projects with accurate shared status
            const actualSharedStatus = projectsSharedStatus[project.id] || false;
            return {
              ...project,
              isShared: actualSharedStatus,
            };
          });

          // Log performance metrics
          const endTime = performance.now();
          const duration = endTime - startTime;
          logger.fetch(`Projects fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

          return updatedProjects;
        } catch (sharedStatusError) {
          // If shared status fetch fails, still return the projects with default shared status
          logger.error(`Error fetching shared status: ${sharedStatusError}`, {
            sharedStatusError,
          });

          // Log performance metrics despite error
          const endTime = performance.now();
          const duration = endTime - startTime;
          logger.fetch(
            `Projects fetch #${fetchId} completed in ${duration.toFixed(2)}ms with shared status error`
          );

          // Still return the projects we loaded
          return formattedProjects;
        }
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchProjects',
            showArchived,
            isOnline,
          },
        });

        throw appError;
      }
    },
  });

  // Note: Realtime subscriptions are handled by the global RealtimeQueryProvider
  // This hook only handles data fetching - subscriptions are centralized

  return query;
}

/**
 * Hook for fetching a single project by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useProject(projectId: string | null) {
  const { data: user, isLoading: userLoading } = useUser();
  const userId = user?.id;
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: projectKeys.detail(projectId || ''),
    enabled: !userLoading && !!userId && !!projectId,
    queryFn: async () => {
      if (!projectId || !userId) {
        const error = new Error('Project ID and user ID are required');
        handleError(error, {
          action: 'silent',
          context: { operation: 'fetchProject', projectId },
        });
        throw error;
      }

      // If offline, get data from IndexedDB
      if (!isOnline) {
        logger.fetch(`Offline mode: fetching project ${projectId} from IndexedDB`);

        try {
          // Get project from IndexedDB
          const project = await db.projects.get(projectId);

          if (!project || project.isDeleted) {
            throw new Error(`Project ${projectId} not found in offline storage`);
          }

          // Format the project
          const formatted = formatProject(project, userId);

          // Set shared status based on ownership
          formatted.isShared = !formatted.isOwner;

          // Apply avatar URL formatting
          formatted.avatar_url = getAvatarUrl(project.avatar_url, project.avatar_updated_at);

          return formatted;
        } catch (offlineError) {
          logger.error(`Error fetching offline project ${projectId}:`, offlineError);
          throw new Error('Failed to load project from offline storage');
        }
      }

      // Online flow - fetch from server
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      // Store in IndexedDB for offline access
      try {
        await db.projects.put(data);
      } catch (storageError) {
        logger.error(`Error storing project in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      // Get project shared status
      const sharedStatus = await secureApi.getProjectsSharedStatus([projectId]);

      // Use the helper function for consistent formatting
      const formatted = formatProject(data as ProjectData, userId);

      // Apply avatar URL formatting
      formatted.avatar_url = getAvatarUrl(data.avatar_url, data.avatar_updated_at);

      // Set shared status based on API response
      formatted.isShared = formatted.isOwner ? sharedStatus[projectId] || false : true; // Non-owner projects are always shared with the current user

      return formatted;
    },
  });
}

/**
 * Hook for creating a new project
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const uploadProjectAvatar = useUploadProjectAvatar();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for project creation
    onMutate: async (newProject: CreateProjectInput & { avatarFile?: File }) => {
      const userId = user?.id;

      if (!userId) {
        return {};
      }

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });

      // Snapshot the previous value for rollback
      const previousProjects = queryClient.getQueryData<Project[]>(projectKeys.lists());

      // Create optimistic project
      const now = new Date().toISOString();
      const optimisticProject: Project = {
        id: newProject.id,
        name: newProject.name,
        description: newProject.description || '',
        context: newProject.context || '',
        user_id: userId,
        last_selected_at: now,
        updated_at: now,
        created_at: now,
        archived: false,
        messages: [],
        lastUpdated: Date.now(),
        isOwner: true,
        isShared: false,
        metadata: {
          client_id: CLIENT_ID,
          created_at: now,
        },
      };

      // Optimistically add the project to all project lists
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return [optimisticProject];
        return [optimisticProject, ...old];
      });

      // Return context for rollback (including the optimistic project for server sync)
      return { previousProjects, optimisticProject };
    },

    mutationFn: async (
      newProject: CreateProjectInput & { avatarFile?: File }
    ): Promise<Project> => {
      const userId = user?.id;

      if (!userId) {
        const error = new Error('User must be authenticated to create a project');
        handleError(error, {
          action: 'toast',
          context: { operation: 'createProject' },
        });
        throw error;
      }

      logger.store('Adding new project', {
        project_id: newProject.id,
        user_id: userId,
        user_id_type: typeof userId,
        has_avatar: !!newProject.avatarFile,
        is_online: isOnline,
      });

      // Extract the avatar file for later use
      const avatarFile = newProject.avatarFile;

      // Create project data object
      const now = new Date().toISOString();
      const projectData: ProjectData = {
        id: newProject.id,
        name: newProject.name,
        description: newProject.description || null,
        context: newProject.context || null,
        user_id: userId,
        last_selected_at: now,
        updated_at: now,
        created_at: now,
        archived: false,
        metadata: {
          client_id: CLIENT_ID,
          created_at: now,
        },
      };

      // If offline, store locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Creating project locally', { projectId: newProject.id });

        try {
          // Store in IndexedDB
          await db.projects.put(projectData);

          // Queue for sync when online
          await queueOfflineOperation('projects', 'create', newProject.id, projectData);

          // Create return object in the same format as online creation
          const createdProject: Project = {
            ...projectData,
            description: projectData.description || '',
            context: projectData.context || '',
            messages: [],
            lastUpdated: new Date(projectData.updated_at).getTime(),
            isOwner: true,
            isShared: false,
          };

          // Note: We can't handle avatar file during offline mode
          // It will be stored when the device comes back online

          return createdProject;
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'createProject', mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - create on server
      const { data, error } = await supabase
        .from('projects')
        .insert([projectData])
        .select()
        .single();

      if (error) throw error;

      logger.store('Project created in database', {
        returned_data: data,
        user_id_in_data: data?.user_id,
        user_id_type: typeof data?.user_id,
      });

      // Store in IndexedDB for offline access
      try {
        await db.projects.put(data);
      } catch (storageError) {
        logger.error(`Error storing project in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      const createdProject = {
        ...data,
        messages: [],
        context: data.context || '',
        lastUpdated: new Date(data.updated_at).getTime(),
        last_selected_at: data.last_selected_at,
        archived: false,
        isOwner: true,
        isShared: false,
      };

      // If an avatar file was provided, upload it now
      if (avatarFile) {
        try {
          // Upload the avatar using the upload hook's mutation function
          const { publicUrl } = await uploadProjectAvatar.mutateAsync({
            projectId: createdProject.id,
            file: avatarFile,
          });

          // Update the project with the avatar URL
          createdProject.avatar_url = publicUrl;
        } catch (avatarError) {
          // Log error but don't fail the project creation - avatar is non-critical
          handleError(avatarError, {
            action: 'silent',
            context: { operation: 'uploadProjectAvatar', projectId: createdProject.id },
          });
          // Continue with the successfully created project
        }
      }

      return createdProject;
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore the previous projects list
      if (context?.previousProjects) {
        queryClient.setQueryData(projectKeys.lists(), context.previousProjects);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to create project. Please try again.',
        context: { operation: 'createProject_optimistic_rollback', projectId: variables.id },
      });
    },

    onSuccess: (newProject, _variables, context) => {
      // Replace the optimistic project with the real one from server
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return [newProject];

        // Find and replace the optimistic project with the real one
        return old.map(project => {
          // Match by the optimistic project data from context
          if (
            context?.optimisticProject &&
            project.name === context.optimisticProject.name &&
            project.created_at === context.optimisticProject.created_at
          ) {
            return newProject;
          }
          return project;
        });
      });

      // Add the new project to the detail cache
      safeUpdateQueryData(queryClient, projectKeys.detail(newProject.id), () => newProject, {
        context: { operation: 'createProject', projectId: newProject.id },
      });
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (data, error, variables) => {
      invalidateQuery(queryClient, projectKeys.lists(), {
        refetchType: 'active',
        context: { operation: 'createProject_settled', projectId: variables.id },
      });
    },
  });
}

/**
 * Hook for updating a project
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for project updates
    onMutate: async ({
      projectId,
      updates,
    }: {
      projectId: string;
      updates: UpdateProjectInput;
    }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(projectId) });

      // Snapshot previous values for rollback
      const previousProjects = queryClient.getQueryData<Project[]>(projectKeys.lists());
      const previousProject = queryClient.getQueryData<Project>(projectKeys.detail(projectId));

      const now = new Date().toISOString();

      // Apply optimistic updates to project list
      if (previousProjects) {
        queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
          if (!old) return old;

          return old.map(project => {
            if (project.id === projectId) {
              const updatedProject = { ...project };
              if (updates.name !== undefined) updatedProject.name = updates.name;
              if (updates.description !== undefined)
                updatedProject.description = updates.description;
              if (updates.context !== undefined) updatedProject.context = updates.context;
              if (updates.avatar_url !== undefined) {
                updatedProject.avatar_url = getAvatarUrl(updates.avatar_url, now);
                updatedProject.avatar_updated_at = now;
              }
              updatedProject.updated_at = now;
              updatedProject.lastUpdated = Date.now();

              return updatedProject;
            }
            return project;
          });
        });
      }

      // Apply optimistic updates to individual project cache
      if (previousProject) {
        queryClient.setQueryData<Project>(projectKeys.detail(projectId), old => {
          if (!old) return old;

          const updatedProject = { ...old };
          if (updates.name !== undefined) updatedProject.name = updates.name;
          if (updates.description !== undefined) updatedProject.description = updates.description;
          if (updates.context !== undefined) updatedProject.context = updates.context;
          if (updates.avatar_url !== undefined) {
            updatedProject.avatar_url = getAvatarUrl(updates.avatar_url, now);
            updatedProject.avatar_updated_at = now;
          }
          updatedProject.updated_at = now;
          updatedProject.lastUpdated = Date.now();

          return updatedProject;
        });
      }

      // Return context for rollback
      return { previousProjects, previousProject };
    },

    mutationFn: async ({
      projectId,
      updates,
    }: {
      projectId: string;
      updates: UpdateProjectInput;
    }) => {
      const now = new Date().toISOString();

      // Filter updates to only include valid fields
      const filteredUpdates = {
        ...(updates.name && { name: updates.name }),
        ...(updates.description && { description: updates.description }),
        ...(updates.context && { context: updates.context }),
        ...(updates.avatar_url && {
          avatar_url: updates.avatar_url,
          avatar_updated_at: now,
        }),
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          updated_at: now,
        },
      };

      // If offline, update locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Updating project locally', { projectId });

        try {
          // Get current project from IndexedDB
          const currentProject = await db.projects.get(projectId);

          if (!currentProject || currentProject.isDeleted) {
            throw new Error(`Project ${projectId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedProject = {
            ...currentProject,
            ...filteredUpdates,
          };

          // Update in IndexedDB
          await db.projects.put(updatedProject);

          // Queue for sync when online
          await queueOfflineOperation('projects', 'update', projectId, updatedProject);

          return { projectId, updates: filteredUpdates, timestamp: now };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateProject', projectId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      const { error } = await supabase.from('projects').update(filteredUpdates).eq('id', projectId);

      if (error) throw error;

      // Update in IndexedDB for offline access
      try {
        // Get current data first
        const currentProject = await db.projects.get(projectId);

        if (currentProject) {
          await db.projects.put({
            ...currentProject,
            ...filteredUpdates,
          });
        }
      } catch (storageError) {
        logger.error(`Error updating project in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { projectId, updates: filteredUpdates, timestamp: now };
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore previous project list
      if (context?.previousProjects) {
        queryClient.setQueryData(projectKeys.lists(), context.previousProjects);
      }

      // Restore previous individual project
      if (context?.previousProject) {
        queryClient.setQueryData(projectKeys.detail(variables.projectId), context.previousProject);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to update project. Your changes have been reverted.',
        context: { operation: 'updateProject_optimistic_rollback', projectId: variables.projectId },
      });
    },

    onSuccess: (result, variables) => {
      // The optimistic update is already applied, the server data will be
      // synchronized through cache invalidation in onSettled
      const projectId = variables.projectId;

      // Update any project detail cache with server data if needed
      safeUpdateQueryData<Project>(
        queryClient,
        projectKeys.detail(projectId),
        currentProject => {
          if (!currentProject) return undefined;

          // Apply any additional server-returned data
          return {
            ...currentProject,
            lastUpdated: Date.now(),
            updated_at: result.timestamp,
          };
        },
        { context: { operation: 'updateProject', projectId } }
      );
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (_data, _error, variables) => {
      // Ensure caches are consistent regardless of outcome
      invalidateQuery(queryClient, projectKeys.lists(), {
        refetchType: 'active',
        context: { operation: 'updateProject_settled', projectId: variables.projectId },
      });

      invalidateQuery(queryClient, projectKeys.detail(variables.projectId), {
        refetchType: 'active',
        context: { operation: 'updateProject_settled', projectId: variables.projectId },
      });
    },
  });
}

/**
 * Hook for deleting a project
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for project deletion
    onMutate: async (projectId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(projectId) });

      // Snapshot previous values for rollback
      const previousProjects = queryClient.getQueryData<Project[]>(projectKeys.lists());
      const previousProject = queryClient.getQueryData<Project>(projectKeys.detail(projectId));

      // Optimistically remove project from projects list
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return old;
        return old.filter(project => project.id !== projectId);
      });

      // Remove project from detail cache
      queryClient.removeQueries({
        queryKey: projectKeys.detail(projectId),
        exact: true,
      });

      // Return context for rollback
      return { previousProjects, previousProject };
    },

    mutationFn: async (projectId: string) => {
      // If offline, mark as deleted locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Marking project as deleted locally', { projectId });

        try {
          // Get current project from IndexedDB
          const currentProject = await db.projects.get(projectId);

          if (!currentProject) {
            logger.warn(`Project ${projectId} not found in offline storage for deletion`);
            return projectId; // Still continue with optimistic UI update
          }

          // Mark as deleted in IndexedDB (we keep the record for sync)
          await db.projects.update(projectId, { isDeleted: true });

          // Queue for sync when online
          await queueOfflineOperation('projects', 'delete', projectId, { id: projectId });

          return projectId;
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'deleteProject', projectId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - delete on server
      const { error } = await supabase.from('projects').delete().eq('id', projectId);

      if (error) throw error;

      // Remove from IndexedDB
      try {
        await db.projects.delete(projectId);
      } catch (storageError) {
        logger.error(`Error deleting project from IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return projectId;
    },

    // On error, rollback the optimistic update
    onError: (error, projectId, context) => {
      // Restore the previous projects list
      if (context?.previousProjects) {
        queryClient.setQueryData(projectKeys.lists(), context.previousProjects);
      }

      // Restore the individual project to detail cache
      if (context?.previousProject) {
        queryClient.setQueryData(projectKeys.detail(projectId), context.previousProject);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to delete project. The project has been restored.',
        context: { operation: 'deleteProject_optimistic_rollback', projectId },
      });
    },

    onSuccess: projectId => {
      // Ensure project is removed from detail cache (already done optimistically, but ensure it's gone)
      queryClient.removeQueries({
        queryKey: projectKeys.detail(projectId),
        exact: true,
      });

      // Ensure the project is removed from projects list (already done optimistically)
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return old;
        return old.filter(project => project.id !== projectId);
      });
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (_data, _error, projectId) => {
      // Ensure caches are consistent regardless of outcome
      invalidateQuery(queryClient, projectKeys.lists(), {
        refetchType: 'active',
        context: { operation: 'deleteProject_settled', projectId },
      });

      // Ensure project detail cache is cleaned up
      queryClient.removeQueries({
        queryKey: projectKeys.detail(projectId),
        exact: true,
      });
    },
  });
}

/**
 * Hook for archiving/unarchiving a project
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useArchiveProject() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for project archiving
    onMutate: async ({ projectId, archive }: { projectId: string; archive: boolean }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });
      await queryClient.cancelQueries({ queryKey: projectKeys.detail(projectId) });

      // Snapshot previous values for rollback
      const previousProjects = queryClient.getQueryData<Project[]>(projectKeys.lists());
      const previousProject = queryClient.getQueryData<Project>(projectKeys.detail(projectId));

      // Optimistically remove/update project in projects list based on archive action
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return old;

        // If archiving, remove from current view
        // If unarchiving, the project won't be in the active list anyway
        return old.filter(project => project.id !== projectId);
      });

      // Update project detail if it exists
      if (previousProject) {
        queryClient.setQueryData<Project>(projectKeys.detail(projectId), old => {
          if (!old) return old;
          return { ...old, archived: archive, lastUpdated: Date.now() };
        });
      }

      // Return context for rollback
      return { previousProjects, previousProject };
    },

    mutationFn: async ({ projectId, archive }: { projectId: string; archive: boolean }) => {
      const now = new Date().toISOString();
      const updates = {
        archived: archive,
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          updated_at: now,
        },
      };

      // If offline, update locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Updating project archive status locally', {
          projectId,
          archive,
        });

        try {
          // Get current project from IndexedDB
          const currentProject = await db.projects.get(projectId);

          if (!currentProject || currentProject.isDeleted) {
            throw new Error(`Project ${projectId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedProject = {
            ...currentProject,
            ...updates,
          };

          // Update in IndexedDB
          await db.projects.put(updatedProject);

          // Queue for sync when online
          await queueOfflineOperation('projects', 'update', projectId, updatedProject);

          return { projectId, archived: archive };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'archiveProject', projectId, archive, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      const { error } = await supabase.from('projects').update(updates).eq('id', projectId);

      if (error) throw error;

      // Update in IndexedDB for offline access
      try {
        // Get current data first
        const currentProject = await db.projects.get(projectId);

        if (currentProject) {
          await db.projects.put({
            ...currentProject,
            ...updates,
          });
        }
      } catch (storageError) {
        logger.error(`Error updating project archive status in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { projectId, archived: archive };
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore the previous projects list
      if (context?.previousProjects) {
        queryClient.setQueryData(projectKeys.lists(), context.previousProjects);
      }

      // Restore the individual project to detail cache
      if (context?.previousProject) {
        queryClient.setQueryData(projectKeys.detail(variables.projectId), context.previousProject);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: variables.archive
          ? 'Failed to archive project. The project has been restored to active status.'
          : 'Failed to restore project. The project remains archived.',
        context: {
          operation: 'archiveProject_optimistic_rollback',
          projectId: variables.projectId,
          archive: variables.archive,
        },
      });
    },

    onSuccess: result => {
      const projectId = result.projectId;

      // Ensure project is removed from current projects list (already done optimistically)
      queryClient.setQueryData<Project[]>(projectKeys.lists(), old => {
        if (!old) return old;
        return old.filter(project => project.id !== projectId);
      });

      // Ensure the project detail is updated with correct archive status
      safeUpdateQueryData<Project>(
        queryClient,
        projectKeys.detail(projectId),
        oldData => {
          if (!oldData) return oldData;
          return { ...oldData, archived: result.archived, lastUpdated: Date.now() };
        },
        { context: { operation: 'archiveProject', projectId } }
      );
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (data, _error, variables) => {
      // Ensure caches are consistent regardless of outcome
      invalidateQuery(queryClient, projectKeys.lists(), {
        refetchType: 'active',
        context: { operation: 'archiveProject_settled', projectId: variables.projectId },
      });

      // Invalidate projects list for the new archive status if successful
      if (data) {
        invalidateQuery(queryClient, projectKeys.list({ archived: data.archived }), {
          refetchType: 'active',
          context: { operation: 'archiveProject_settled', projectId: variables.projectId },
        });
      }

      invalidateQuery(queryClient, projectKeys.detail(variables.projectId), {
        refetchType: 'active',
        context: { operation: 'archiveProject_settled', projectId: variables.projectId },
      });
    },
  });
}

/**
 * Hook for uploading a project avatar
 * Only works online since it requires storage access
 */
export function useUploadProjectAvatar() {
  const updateProject = useUpdateProject();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ projectId, file }: { projectId: string; file: File }) => {
      // Validate file type - only allow supported image formats (from centralized constants)
      if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
        const error = new Error(
          `Unsupported image format: ${file.type}. Please use JPEG, PNG, GIF, or WebP.`
        );
        handleError(error, {
          action: 'toast',
          customMessage: 'Please upload a JPEG, PNG, GIF, or WebP image. SVG is not supported.',
          context: { operation: 'uploadAvatar', projectId, fileType: file.type },
        });
        throw error;
      }

      // Check if online - avatar uploads require network
      if (!isOnline) {
        const error = new Error('Cannot upload avatar while offline');
        handleError(error, {
          action: 'toast',
          context: { operation: 'uploadAvatar', projectId },
        });
        throw error;
      }

      try {
        // Resize the image first
        const resizedBlob = await resizeImage(file, 1000);
        const resizedFile = new File([resizedBlob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });

        const fileName = `${projectId}/avatar.${file.type.split('/')[1]}`;

        // Try to remove existing avatar first
        try {
          const { error: removeError } = await supabase.storage
            .from('project-avatars')
            .remove([fileName]);

          if (removeError) {
            console.warn('Error removing existing avatar:', removeError);
          }
        } catch (removeError) {
          console.warn('Error in remove operation:', removeError);
        }

        // Upload new avatar
        const { error: uploadError } = await supabase.storage
          .from('project-avatars')
          .upload(fileName, resizedFile, {
            upsert: true,
            contentType: file.type,
            cacheControl: '3600',
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        // Get public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from('project-avatars').getPublicUrl(fileName);

        if (!publicUrl) {
          throw new Error('Failed to get public URL for uploaded avatar');
        }

        return { projectId, publicUrl };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'uploadProjectAvatar', projectId },
        });
        throw appError;
      }
    },
    onSuccess: result => {
      // Update the project with the new avatar URL
      updateProject.mutate({
        projectId: result.projectId,
        updates: { avatar_url: result.publicUrl },
      });
    },
  });
}

/**
 * Hook for updating a project's last selected timestamp
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateLastSelectedAt() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;

  return useMutation({
    mutationFn: async (projectId: string) => {
      if (!projectId) return null;

      const now = new Date().toISOString();
      const updates = {
        last_selected_at: now,
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          updated_at: now,
        },
      };

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current project from IndexedDB
          const currentProject = await db.projects.get(projectId);

          if (!currentProject || currentProject.isDeleted) {
            logger.warn(`Project ${projectId} not found in offline storage for timestamp update`);
            return { projectId, timestamp: now }; // Continue with optimistic UI update
          }

          // Merge updates with current data
          const updatedProject = {
            ...currentProject,
            ...updates,
          };

          // Update in IndexedDB
          await db.projects.put(updatedProject);

          // Queue for sync when online
          await queueOfflineOperation('projects', 'update', projectId, updatedProject);

          return { projectId, timestamp: now };
        } catch (offlineError) {
          logger.error('Error updating timestamp in offline mode:', offlineError);
          // Don't throw - this is non-critical
          return { projectId, timestamp: now }; // Return for optimistic UI update
        }
      }

      // Online flow - update on server
      try {
        await supabase.from('projects').update(updates).eq('id', projectId);

        // Update in IndexedDB for offline access
        try {
          // Get current data first
          const currentProject = await db.projects.get(projectId);

          if (currentProject) {
            await db.projects.put({
              ...currentProject,
              ...updates,
            });
          }
        } catch (storageError) {
          logger.error(`Error updating timestamp in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { projectId, timestamp: now };
      } catch (error) {
        logger.error('❌ Error updating last_selected_at:', error);
        throw error;
      }
    },
    onSuccess: result => {
      if (!result) return;

      const projectId = result.projectId;
      const timestamp = result.timestamp;
      const now = Date.now();

      // Use batch update pattern for improved performance
      const updateDetailSuccess = safeUpdateQueryData<Project>(
        queryClient,
        projectKeys.detail(projectId),
        oldData => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            last_selected_at: timestamp,
            lastUpdated: now,
          };
        },
        { context: { operation: 'updateLastSelectedAt', projectId } }
      );

      // Only update lists if detail update was successful
      if (updateDetailSuccess) {
        // Update the project in the projects list with efficient cache update
        safeUpdateQueryData<Project[]>(
          queryClient,
          projectKeys.lists(),
          oldData => {
            if (!oldData) return oldData;

            // Check if project exists in the list before mapping
            const projectExists = oldData.some(p => p.id === projectId);
            if (!projectExists) return oldData;

            return oldData.map(project =>
              project.id === projectId
                ? {
                    ...project,
                    last_selected_at: timestamp,
                    lastUpdated: now,
                  }
                : project
            );
          },
          { context: { operation: 'updateLastSelectedAt', projectId } }
        );
      }
    },
  });
}
